import { useEffect, useState } from 'react'
import { billingApiService } from '../../services/billingApiService'
import { ApiError } from '../../services/BaseApiService'
import { BillingPeriodStatus, type BillingPeriod } from '../../types'
import type { BillLine, CurrentBill as CurrentBillData, CurrentBillResponse, PastBill } from '../../types/billingStatement'
import { formatCents, overageSummary } from '../../utils/billingStanding'
import { colors, fontSize, radius, shadow, spacing } from '../../styles/theme'

/**
 * The Billing page's statement: this month's bill so far, itemised as the monthly run will invoice it (the API
 * prices it with the same calculation), a separately labelled end-of-month estimate, and past bills with their
 * lines. Itemised money needs Manage billing; anyone else sees past totals only (from the status read).
 */

const card: React.CSSProperties = {
  background: colors.surface,
  border: `1px solid ${colors.border}`,
  borderRadius: radius.lg,
  boxShadow: shadow.sm,
  padding: spacing.xl,
  marginBottom: spacing.lg,
}

const muted: React.CSSProperties = { fontSize: fontSize.sm, color: colors.textSecondary }
const cell: React.CSSProperties = { padding: `${spacing.xs} 0`, verticalAlign: 'top' }
const num: React.CSSProperties = { ...cell, textAlign: 'right', whiteSpace: 'nowrap', paddingLeft: spacing.md }

function day(iso: string): string {
  // Period dates are calendar dates (yyyy-mm-dd); read them as such, not as UTC midnight shifted into local time.
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

function month(iso: string): string {
  const [y, m] = iso.slice(0, 10).split('-').map(Number)
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
}

/** The last day of a period whose end is exclusive (the 1st of the next month). */
function lastDay(endIso: string): string {
  const [y, m, d] = endIso.slice(0, 10).split('-').map(Number)
  return new Date(y, m - 1, d - 1).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

export function periodStatusLabel(status: number): { text: string; color: string } {
  switch (status) {
    case BillingPeriodStatus.PAID:            return { text: 'Paid', color: colors.success }
    case BillingPeriodStatus.FAILED:          return { text: 'Payment failed', color: colors.error }
    case BillingPeriodStatus.CHARGING:        return { text: 'Charging', color: colors.warning }
    case BillingPeriodStatus.CARRIED_FORWARD: return { text: 'Carried to next month', color: colors.textSecondary }
    case BillingPeriodStatus.ZERO:            return { text: 'No usage', color: colors.textMuted }
    case BillingPeriodStatus.DEFERRED:        return { text: 'Deferred while on Free', color: colors.textSecondary }
    default:                                  return { text: 'Pending', color: colors.textSecondary }
  }
}

function Lines({ lines }: { lines: BillLine[] }) {
  return (
    <>
      {lines.map((l, i) => (
        <tr key={i}>
          <td style={cell}>{l.label}</td>
          <td style={{ ...num, color: colors.textSecondary }}>
            {l.quantity.toLocaleString()} × {formatCents(l.unitPriceCents)}
          </td>
          <td style={num}>{formatCents(l.amountCents)}</td>
        </tr>
      ))}
    </>
  )
}

function Row({ label, value, strong, testId }: { label: string; value: string; strong?: boolean; testId?: string }) {
  return (
    <tr data-testid={testId}>
      <td style={{ ...cell, fontWeight: strong ? 700 : 400 }} colSpan={2}>{label}</td>
      <td style={{ ...num, fontWeight: strong ? 700 : 400 }}>{value}</td>
    </tr>
  )
}

function Current({ bill }: { bill: CurrentBillData }) {
  const unit = bill.includedPages != null || bill.perPageCents != null ? 'pages' : 'documents'
  const used = unit === 'pages' ? bill.pages : bill.documents
  const included = unit === 'pages' ? bill.includedPages : bill.includedDocuments

  return (
    <div style={card} data-testid="current-bill">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: spacing.sm, flexWrap: 'wrap', marginBottom: spacing.xs }}>
        <div style={{ fontSize: fontSize.lg, fontWeight: 600 }}>Current bill</div>
        <div style={muted}>{day(bill.periodStart)} – {lastDay(bill.periodEnd)}</div>
      </div>
      {!bill.noCharge && bill.disposition === 'billed' && (
        <div style={{ ...muted, marginBottom: spacing.md }}>Month to date · bills on {day(bill.billsOn)}</div>
      )}

      <div style={{ ...muted, marginBottom: spacing.md }}>
        {used.toLocaleString()} {unit} used
        {included != null && <> of {included.toLocaleString()} included</>}
        {unit === 'pages' && <> · {bill.documents.toLocaleString()} documents</>}
        {overageSummary(bill, bill.appliesOverageSettings, bill.perPageCents, bill.includedPages) && (
          <div data-testid="bill-overage">{overageSummary(bill, bill.appliesOverageSettings, bill.perPageCents, bill.includedPages)}</div>
        )}
        {bill.overageCappedFromCents != null && bill.allowOverage !== false && bill.overageLimitDollars != null && (
          <div data-testid="bill-overage-capped" style={{ color: colors.warning }}>
            Page overage capped at your ${bill.overageLimitDollars.toLocaleString()} monthly limit
            (uncapped, the page overage would be {formatCents(bill.overageCappedFromCents)}).
          </div>
        )}
        {bill.overageCappedFromCents != null && bill.allowOverage === false && (
          <div data-testid="bill-overage-capped" style={{ color: colors.warning }}>
            Pages past the included {included?.toLocaleString()} are not charged while overage is off
            (at the per-page rate they would be {formatCents(bill.overageCappedFromCents)}).
          </div>
        )}
      </div>

      {bill.noCharge ? (
        <div style={{ fontSize: fontSize.base, fontWeight: 600 }}>
          {bill.planCode === 'free'
            ? "No charge — your team's plan is assigned by DocMorphia."
            : "No charge — your team isn't billed."}
        </div>
      ) : bill.disposition === 'trial' ? (
        <div>
          <div style={{ fontSize: fontSize.base, fontWeight: 600, marginBottom: spacing.xs }}>
            Free trial — this month isn't billed.
          </div>
          {bill.trialBillingStarts && (
            <div style={muted}>Billing starts with {month(bill.trialBillingStarts)}.</div>
          )}
          {bill.usageCharges.length > 0 && (
            <>
              <div style={{ ...muted, marginTop: spacing.md }}>
                These charges will be on your first bill, for {bill.trialBillingStarts ? month(bill.trialBillingStarts) : 'your first billed month'}:
              </div>
              <UsageCharges bill={bill} />
            </>
          )}
        </div>
      ) : bill.disposition === 'refused' ? (
        <div style={muted} data-testid="bill-refused">
          {bill.refusedBecause === 'incomplete_page_counts'
            ? "Some of this month's documents have no recorded page count, so this month can't be estimated. Contact support."
            : "Your plan's pricing isn't set up yet, so this month can't be estimated. Contact support."}
        </div>
      ) : (
        <>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: fontSize.sm }}>
            <tbody>
              {/* Plan and usage lines, then a balance carried from before Free (kind 40); AI runs are listed as dated charges below. */}
              <Lines lines={bill.lines.filter(l => l.usageKind < 30 || l.usageKind === 40)} />
              {bill.usageCharges.length > 0 && (
                <tr><td colSpan={3} style={{ ...cell, paddingTop: spacing.sm, fontWeight: 600 }}>AI translation</td></tr>
              )}
              {bill.usageCharges.map((c, i) => (
                <tr key={`ai-${i}`} data-testid="ai-charge">
                  <td style={cell} colSpan={2}>
                    {new Date(c.date).toLocaleDateString()} · {c.template ?? 'Template'}
                    {c.locales.length > 0 && <> · {c.locales.join(', ')}</>}
                  </td>
                  <td style={num}>{formatCents(c.amountCents)}</td>
                </tr>
              ))}
              <tr><td colSpan={3} style={{ borderTop: `1px solid ${colors.border}`, padding: 0 }} /></tr>
              <Row label="Subtotal" value={formatCents(bill.subtotalCents)} testId="subtotal" />
              {bill.carriedInCents > 0 && (
                <Row label="Carried from an earlier month" value={formatCents(bill.carriedInCents)} />
              )}
              {bill.creditAppliedCents > 0 && (
                <Row label="Account credit" value={`−${formatCents(bill.creditAppliedCents)}`} testId="credit" />
              )}
              <Row label="Total so far" value={formatCents(bill.totalCents)} strong testId="total" />
            </tbody>
          </table>
          {bill.belowMinimumCharge && (
            <div style={{ ...muted, marginTop: spacing.sm }}>
              Totals under {formatCents(bill.minimumChargeCents)} aren't charged; they carry to the next month.
            </div>
          )}
          {bill.creditBalanceCents > bill.creditAppliedCents && (
            <div style={{ ...muted, marginTop: spacing.xs }}>
              {formatCents(bill.creditBalanceCents - bill.creditAppliedCents)} of account credit remains after this bill.
            </div>
          )}
          {bill.projection && (
            <div data-testid="projection" style={{
              marginTop: spacing.md, padding: spacing.md, borderRadius: radius.sm,
              border: `1px dashed ${colors.border}`, background: colors.background,
            }}>
              <div style={{ fontSize: fontSize.sm, fontWeight: 600 }}>
                Estimate for the full month: {formatCents(bill.projection.projectedTotalCents)}
              </div>
              <div style={muted}>
                An estimate, not a charge: it assumes you keep rendering at this month's pace
                (about {bill.projection.projectedPages.toLocaleString()} pages by month end).
              </div>
            </div>
          )}
        </>
      )}

      {bill.activity.length > 0 && (
        <div style={{ marginTop: spacing.lg }}>
          <div style={{ fontSize: fontSize.sm, fontWeight: 600, marginBottom: spacing.xs }}>Other billing activity this month</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: fontSize.sm }}>
            <tbody>
              {bill.activity.map((a, i) => (
                <tr key={i} data-testid="activity">
                  <td style={cell}>{new Date(a.date).toLocaleDateString()}</td>
                  <td style={{ ...cell, paddingLeft: spacing.md }}>
                    {{ credit: 'Credit added', refund: 'Refund', charge: 'One-off charge', invoice: 'Invoice' }[a.kind]}: {a.description}
                    {a.status && <span style={{ color: colors.textSecondary }}> ({a.status})</span>}
                  </td>
                  <td style={num}>{a.kind === 'credit' || a.kind === 'refund' ? '−' : ''}{formatCents(a.amountCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ ...muted, marginTop: spacing.xs }}>Credit added is applied to your next bill automatically.</div>
        </div>
      )}
    </div>
  )
}

function UsageCharges({ bill }: { bill: CurrentBillData }) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: fontSize.sm, marginTop: spacing.xs }}>
      <tbody>
        {bill.usageCharges.map((c, i) => (
          <tr key={i} data-testid="ai-charge">
            <td style={cell}>
              {new Date(c.date).toLocaleDateString()} · {c.template ?? 'Template'}
              {c.locales.length > 0 && <> · {c.locales.join(', ')}</>}
            </td>
            <td style={num}>{formatCents(c.amountCents)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function PastBills({ past }: { past: PastBill[] }) {
  const [open, setOpen] = useState<string | null>(null)
  return (
    <div style={card}>
      <div style={{ fontSize: fontSize.lg, fontWeight: 600, marginBottom: spacing.md }}>Past bills</div>
      {past.length === 0 ? (
        <div style={{ color: colors.textMuted, fontSize: fontSize.sm }}>Nothing has been billed yet.</div>
      ) : past.map(p => {
        const label = periodStatusLabel(p.status)
        const expanded = open === p.billingPeriodId
        return (
          <div key={p.billingPeriodId} style={{ borderBottom: `1px solid ${colors.border}` }}>
            <button
              type="button"
              aria-expanded={expanded}
              onClick={() => setOpen(expanded ? null : p.billingPeriodId)}
              style={{
                display: 'flex', width: '100%', alignItems: 'center', gap: spacing.md, background: 'none', border: 'none',
                padding: `${spacing.sm} 0`, cursor: 'pointer', fontSize: fontSize.sm, color: colors.text, textAlign: 'left',
              }}
            >
              <span style={{ width: 12, color: colors.textSecondary }}>{expanded ? '▾' : '▸'}</span>
              <span style={{ flex: 1 }}>{month(p.periodStart)}</span>
              <span style={{ color: label.color }}>{label.text}</span>
              <span style={{ fontWeight: 600, minWidth: 80, textAlign: 'right' }}>{formatCents(p.totalCents)}</span>
            </button>
            {expanded && (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: fontSize.sm, marginBottom: spacing.sm }}>
                <tbody>
                  <Lines lines={p.lines} />
                  <Row label="Subtotal" value={formatCents(p.subtotalCents)} />
                  {p.carriedInCents > 0 && <Row label="Carried from an earlier month" value={formatCents(p.carriedInCents)} />}
                  {p.creditAppliedCents > 0 && <Row label="Account credit" value={`−${formatCents(p.creditAppliedCents)}`} />}
                  <Row label="Total" value={formatCents(p.totalCents)} strong />
                  {p.status === BillingPeriodStatus.CARRIED_FORWARD && p.carriedIntoPeriodStart && (
                    <tr data-testid="carried-into"><td colSpan={2} style={{ ...muted, paddingTop: spacing.xs }}>
                      Under the minimum charge, so not charged on its own: carried into your {month(p.carriedIntoPeriodStart)} bill and counted there, once.
                    </td></tr>
                  )}
                  {p.status === BillingPeriodStatus.DEFERRED && (
                    <tr><td colSpan={2} style={{ ...muted, paddingTop: spacing.xs }}>
                      Deferred when your team moved to Free. Not collected while on Free; billed once, as “Balance carried from before Free”, on your first bill after returning to a paid plan.
                    </td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        )
      })}
    </div>
  )
}

/** Past totals only, for a member without Manage billing (the status read's history). */
function HistoryTotals({ periods }: { periods: BillingPeriod[] }) {
  return (
    <div style={card}>
      <div style={{ fontSize: fontSize.lg, fontWeight: 600, marginBottom: spacing.md }}>History</div>
      {periods.length ? (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: fontSize.sm }}>
          <tbody>
            {periods.map(p => {
              const label = periodStatusLabel(p.status)
              return (
                <tr key={p.periodStart} style={{ borderBottom: `1px solid ${colors.border}` }}>
                  <td style={{ padding: `${spacing.sm} 0` }}>{month(p.periodStart)}</td>
                  <td style={{ padding: `${spacing.sm} 0`, color: label.color }}>{label.text}</td>
                  <td style={{ padding: `${spacing.sm} 0`, textAlign: 'right', fontWeight: 600 }}>{formatCents(p.totalCents)}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      ) : (
        <div style={{ color: colors.textMuted, fontSize: fontSize.sm }}>Nothing has been billed yet.</div>
      )}
      <div style={{ ...muted, marginTop: spacing.sm }}>A team admin can see the itemised current bill.</div>
    </div>
  )
}

export interface CurrentBillLoad { data: CurrentBillResponse | null; forbidden: boolean; error: string }

/** Loads the current bill once per `reloadKey`; the Billing page shares it between "This month" and the statement. */
export function useCurrentBill(reloadKey = 0): CurrentBillLoad {
  const [state, setState] = useState<CurrentBillLoad>({ data: null, forbidden: false, error: '' })

  useEffect(() => {
    let live = true
    billingApiService.getCurrentBill()
      .then(r => { if (live) setState({ data: r, forbidden: false, error: '' }) })
      .catch(err => {
        if (!live) return
        if (err instanceof ApiError && err.status === 403) setState({ data: null, forbidden: true, error: '' })
        else setState({ data: null, forbidden: false, error: err instanceof ApiError ? err.message : 'Could not load the current bill' })
      })
    return () => { live = false }
  }, [reloadKey])

  return state
}

export default function CurrentBill({ periods, load }: { periods: BillingPeriod[]; load: CurrentBillLoad }) {
  const { data, forbidden, error } = load

  if (forbidden) return <HistoryTotals periods={periods} />
  if (error) return <div style={{ ...card, color: colors.error, fontSize: fontSize.sm }}>{error}</div>
  if (!data) return <div style={{ ...card, ...muted }}>Loading the current bill…</div>

  return (
    <>
      <Current bill={data.current} />
      <PastBills past={data.past} />
    </>
  )
}
